import {Component, output, signal, inject, OnInit, effect} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {TrajetDto} from '../../../../shared/dto/trajet.dto';
import {TrajetService} from '../../../../shared/services/trajet.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {AlertService} from 'sigale-angular';
import {TranslateService} from '@ngx-translate/core';
import {TrajetSelectionneService} from '../services/trajet-selectionne.service';
import {isNotNullOrUndefined} from '@sigale-angular/src/utils/functions';
import {Observable} from 'rxjs';
import {RafraichissementTrajetsService} from '../services/rafraichissement-trajets.service';

@Component({
	selector: 'app-creation-modification-trajet',
	templateUrl: './creation-modification-trajet.component.html',
	styleUrl: './creation-modification-trajet.component.scss'
})
export class CreationModificationTrajetComponent implements OnInit {
	// Constantes
	readonly NOM_ALERTE_TRAJETS = "alerteTabTrajets";

	// Injections
	private alertService = inject(AlertService);
	private confirmationService = inject(ConfirmationService);
	private messageService = inject(MessageService);
	private rafraichissementTrajets = inject(RafraichissementTrajetsService);
	private trajetSelectionneService = inject(TrajetSelectionneService);
	private trajetService = inject(TrajetService);
	private translateService = inject(TranslateService);

	// Outputs
	fermerCreationModificationEvent = output<void>();

	// Signaux
	creationModificationEnCours = signal(false);
	estEnModeModification = signal(false);

	// Variables
	trajetAModifier: TrajetDto;

	constructor() {
		this.ecouterChangementTrajetSelectionne();
	}

	ngOnInit() {
		if (isNotNullOrUndefined(this.trajetSelectionneService.trajetSelectionne())) {
			this.trajetAModifier = this.trajetSelectionneService.trajetSelectionne();
			this.estEnModeModification.set(true);
		}
	}

	/**
	 * Écoute les changements de trajet sélectionné dans le service et ferme la création si un trajet est sélectionné.
	 */
	ecouterChangementTrajetSelectionne(): void {
		effect(() => {
			const enModif = isNotNullOrUndefined(this.trajetAModifier);
			const enModificationEtChangementTrajetSelectionne = enModif && this.trajetAModifier?.id != this.trajetSelectionneService.trajetSelectionne()?.id;
			const enCreationEtTrajetSelectionne = !enModif && isNotNullOrUndefined(this.trajetSelectionneService.trajetSelectionne());
			if (enCreationEtTrajetSelectionne || enModificationEtChangementTrajetSelectionne) {
				this.onFermer();
			}
		});
	}

	/**
	 * Ouvre un popup de confirmation pour valider l'annulation de la création/modification d'un trajet.
	 */
	onFermer() {
		let mode = this.estEnModeModification() ? 'MODIFICATION' : 'CREATION';
		let message = `GESTION_NAVETTE.TRAJETS.MESSAGE_ANNULATION_${mode}`;
		let header = `GESTION_NAVETTE.ANNULATION_${mode}.ANNULER_${mode}`;
		let acceptLabel = `GESTION_NAVETTE.ANNULATION_${mode}.ANNULER_${mode}`;
		let rejectLabel = `GESTION_NAVETTE.ANNULATION_${mode}.REVENIR_${mode}`;

		this.confirmationService.confirm({
			message: this.translateService.instant(message),
			header: this.translateService.instant(header),
			acceptLabel: this.translateService.instant(acceptLabel),
			rejectLabel: this.translateService.instant(rejectLabel),
			acceptButtonStyleClass: 'p-button-danger',
			rejectButtonStyleClass: 'p-button-link',
			accept: () => this.fermerCreationModification(),
			reject: () => this.estEnModeModification()
				? this.trajetSelectionneService.enregistrerTrajetSelectionne(this.trajetAModifier)
				: this.trajetSelectionneService.deselectionnerTrajet()
		});
	}

	/**
	 * Réinitialise le formulaire et notifie le parent pour fermer la section création/modification d'un trajet.
	 */
	fermerCreationModification() {
		this.rafraichissementTrajets.demanderRafraichissement();
		this.fermerCreationModificationEvent.emit();
	}

	/**
	 * Modifie ou crée le trajet à partir des données du formulaire, selon le mode dans lequel on est.
	 */
	submitFormulaire(trajet: TrajetDto): void {
		this.creationModificationEnCours.set(true);
		this.alertService.clear(this.NOM_ALERTE_TRAJETS);
		this.editionTrajet(trajet).pipe(
			finalize(() => this.creationModificationEnCours.set(false))
		).subscribe({
			next: () => {
				const cleTraductionSucces = this.estEnModeModification()
					? 'GESTION_NAVETTE.FORMULAIRE.MESSAGE_MODIFICATION_TRAJET'
					: 'GESTION_NAVETTE.FORMULAIRE.MESSAGE_CREATION_TRAJET';
				this.messageService.add({ severity: 'success', detail: this.translateService.instant(cleTraductionSucces) });

				this.fermerCreationModification();
			},
			error: (erreur) => {
				this.alertService.error(erreur.error, this.NOM_ALERTE_TRAJETS);
			}
		});
	}

	/**
	 * Retourne la méthode du service à appeler pour créer ou modifier le trajet selon le mode dans lequel on est.
	 *
	 * @param trajet le dto sur lequel réaliser la création ou modification.
	 * @returns {Observable<void>} l'observable résultant de la méthode du service appelée.
	 */
	private editionTrajet(trajet: TrajetDto): Observable<void> {
		if (this.estEnModeModification() && isNotNullOrUndefined(this.trajetAModifier?.id)) {
			trajet.id = this.trajetAModifier.id;
			return this.trajetService.mettreAJourTrajet(trajet);
		}
		return this.trajetService.creerTrajet(trajet);
	}
}
